import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import i18n from '../translations';
import { IExercise, IMeditation, languageType } from '../types';
import { LikeMindedState } from './store';

interface ContentLibraryResponse {
  audio: Array<IExercise>;
  meditation: Array<IMeditation>;
}

interface ContentLibrarySlice {
  loading: boolean;
  exercises: Array<IExercise>;
  meditations: Array<IMeditation>;
}

const initialState = {
  loading: false,
  exercises: [],
  meditations: [],
} as ContentLibrarySlice;

export const fetchContentLibrary = createAsyncThunk<
  ContentLibraryResponse,
  void,
  {
    rejectValue: number;
  }
>('app/contentLibrary', async (_, { rejectWithValue }) => {
  try {
    const contentLibraryURL =
      i18n.language === languageType.ENGLISH
        ? process.env.REACT_APP_CONTENT_LIBRARY_EN
        : process.env.REACT_APP_CONTENT_LIBRARY_DE;

    const response = await fetch(contentLibraryURL!).then(
      (response) => (response.json() as unknown) as ContentLibraryResponse,
    );

    return response;
  } catch (e) {
    Sentry.captureException(e);
    return rejectWithValue(401);
  }
});

export const contentLibrarySlice = createSlice({
  name: 'contentLibrary',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchContentLibrary.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchContentLibrary.fulfilled, (state, action) => {
      state.loading = false;
      state.exercises = action.payload.audio;
      state.meditations = action.payload.meditation;
    });
    builder.addCase(fetchContentLibrary.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const contentLibraryReducer = contentLibrarySlice.reducer;
export const selectContentLibrary = (state: LikeMindedState) =>
  state.contentLibrary;
